"use client";

import { Play } from "lucide-react";
import Image from "next/image";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { extractYouTubeId } from "@/lib/utils/oembed";

interface YouTubeEmbedProps {
	url: string;
	width?: number;
	height?: number;
	/** Fill a parent that already defines 16:9 (e.g. `aspect-video` wrapper) */
	fill?: boolean;
	className?: string;
	onLoaded?: () => void;
	/** Start playback when iframe loads (e.g. after user opens immersive player) */
	autoplay?: boolean;
	/** Native iframe loading hint */
	iframeLoading?: "lazy" | "eager";
}

const thumbnailSizes = ["maxresdefault", "sddefault", "hqdefault"] as const;

function YouTubePreview({
	videoId,
	onPlay,
	onLoaded,
}: {
	videoId: string;
	onPlay: () => void;
	onLoaded?: () => void;
}) {
	const [thumbnailIndex, setThumbnailIndex] = useState(0);
	const [imageFailed, setImageFailed] = useState(false);
	const thumbnailUrl = `https://i.ytimg.com/vi/${videoId}/${thumbnailSizes[thumbnailIndex]}.jpg`;

	const tryNextThumbnail = () => {
		if (thumbnailIndex < thumbnailSizes.length - 1) {
			setThumbnailIndex((index) => index + 1);
		} else {
			setImageFailed(true);
		}
	};

	return (
		<button
			type="button"
			onClick={onPlay}
			aria-label="Play YouTube video"
			className="group relative block h-full w-full overflow-hidden bg-black text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black dark:focus-visible:outline-white"
		>
			{!imageFailed ? (
				<Image
					src={thumbnailUrl}
					alt=""
					fill
					unoptimized
					sizes="(max-width: 768px) 100vw, 728px"
					className="object-cover"
					onLoad={(event) => {
						// Unavailable YouTube sizes can return a tiny placeholder image.
						if (event.currentTarget.naturalWidth < 320) {
							tryNextThumbnail();
						} else {
							onLoaded?.();
						}
					}}
					onError={tryNextThumbnail}
				/>
			) : null}
			<span className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20">
				<span className="flex size-14 items-center justify-center rounded-full bg-black/75 shadow-lg ring-1 ring-white/25 transition-transform group-hover:scale-[1.05]">
					<Play
						className="ml-0.5 size-6 fill-current"
						strokeWidth={0}
						aria-hidden="true"
					/>
				</span>
			</span>
		</button>
	);
}

export function YouTubeEmbed({
	url,
	width = 560,
	height = 315,
	fill,
	className,
	onLoaded,
	autoplay = false,
	iframeLoading = "lazy",
}: YouTubeEmbedProps) {
	const videoId = extractYouTubeId(url);
	const [activeVideoId, setActiveVideoId] = useState<string | null>(null);

	if (!videoId) {
		return (
			<div className="border rounded-lg p-4 bg-gray-100 dark:bg-gray-800">
				<p className="text-sm text-muted-foreground">Invalid YouTube URL</p>
			</div>
		);
	}

	const showPlayer = autoplay || activeVideoId === videoId;
	const embedUrl = new URL(`https://www.youtube.com/embed/${videoId}`);
	if (showPlayer) {
		embedUrl.searchParams.set("autoplay", "1");
	}
	const allow =
		"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture";

	return (
		<div
			className={cn(
				fill ? "absolute inset-0 h-full w-full" : "aspect-video w-full",
				"overflow-hidden bg-black",
				className,
			)}
			style={fill ? undefined : { maxHeight: height * 2 }}
		>
			{showPlayer ? (
				<iframe
					src={embedUrl.toString()}
					width={width}
					height={height}
					title="YouTube video player"
					allow={allow}
					allowFullScreen
					loading={iframeLoading}
					onLoad={onLoaded}
					className="h-full w-full border-0"
				/>
			) : (
				<YouTubePreview
					key={videoId}
					videoId={videoId}
					onPlay={() => setActiveVideoId(videoId)}
					onLoaded={onLoaded}
				/>
			)}
		</div>
	);
}
